import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { formatDateTime, formatNumber } from '../utils/format';
import {
  FaBars,
  FaBell,
  FaBuilding,
  FaChartBar,
  FaChevronDown,
  FaCog,
  FaEnvelopeOpenText,
  FaIdCard,
  FaMoon,
  FaProjectDiagram,
  FaSignOutAlt,
  FaSun,
  FaTicketAlt,
  FaTimes,
  FaUser,
  FaUsers
} from 'react-icons/fa';
import { normalizeRole } from '../utils/rbac';

/* ─── Role Utilities ──────────────────────────────────────── */
const ROLE_LABELS = {
  admin: 'مدير النظام',
  supervisor: 'مشرف',
  technician: 'فني',
  viewer: 'مستخدم عرض'
};

/* ─── Navigation Config ───────────────────────────────────── */
const buildNavItems = (role) => {
  const supportRoles = ['admin', 'supervisor', 'technician', 'viewer'];
  const mgmtRoles = ['admin', 'supervisor'];

  const all = [
    // Exact primary navigation order:
    // Dashboard, Inbox, Tickets, Projects, Reports, Settings
    {
      id: 'dashboard',
      label: 'لوحة التحكم',
      icon: <FaChartBar />,
      to: '/dashboard',
      section: 'support',
      roles: supportRoles
    },
    {
      id: 'incoming-emails',
      label: 'البريد الوارد',
      icon: <FaEnvelopeOpenText />,
      to: '/incoming-emails',
      section: 'support',
      roles: ['admin', 'supervisor', 'technician']
    },
    {
      id: 'tickets',
      label: 'التذاكر',
      icon: <FaTicketAlt />,
      to: '/tickets',
      section: 'support',
      roles: supportRoles
    },
    {
      id: 'projects',
      label: 'المشاريع',
      icon: <FaProjectDiagram />,
      to: '/projects',
      section: 'support',
      roles: ['admin', 'supervisor', 'technician', 'viewer']
    },
    {
      id: 'reports',
      label: 'التقارير',
      icon: <FaChartBar />,
      to: '/reports',
      section: 'support',
      roles: ['admin', 'supervisor', 'technician', 'viewer']
    },
    // — Settings (accordion group)
    {
      id: 'settings',
      label: 'الإعدادات',
      icon: <FaCog />,
      section: 'settings',
      type: 'group',
      roles: [...mgmtRoles],
      children: [
        {
          id: 'staff',
          label: 'المستخدمون / الفنيون',
          icon: <FaUsers />,
          to: '/staff',
          roles: [...mgmtRoles]
        },
        {
          id: 'branches',
          label: 'الفروع',
          icon: <FaBuilding />,
          to: '/branches',
          roles: mgmtRoles
        },
        {
          id: 'employee-badges',
          label: 'بطاقات التعريف',
          icon: <FaIdCard />,
          to: '/employee-badges',
          roles: [...mgmtRoles]
        }
      ]
    }
  ];

  return all
    .filter((item) => item.roles?.includes(role))
    .map((item) => {
      if (!item.children) return item;
      return {
        ...item,
        children: item.children.filter((c) => c.roles?.includes(role))
      };
    })
    .filter((item) => {
      if (item.type !== 'group') return true;
      return item.children?.length > 0;
    });
};

/* ─── Layout Component ────────────────────────────────────── */
const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    resolveNotificationTarget,
    validateNotificationTarget,
    onlineUsers
  } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const role = normalizeRole(user?.role);
  const navItems = useMemo(() => buildNavItems(role), [role]);
  const roleLabel = ROLE_LABELS[role] || ROLE_LABELS.viewer;
  const settingsSectionLabel = 'الإعدادات';
  const settingsTriggerLabel = 'الإعدادات';

  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'notifications' | 'onlineUsers' | 'userMenu'
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  // Refs for outside-click detection
  const mobileDrawerRef = useRef(null);
  const notifRef = useRef(null);
  const onlineRef = useRef(null);
  const userMenuRef = useRef(null);
  const settingsRef = useRef(null);

  /* ─── Derived navigation sections ────────────────────────── */
  const navSections = useMemo(() => ({
    core: navItems.filter((i) => i.section === 'core'),
    support: navItems.filter((i) => i.section === 'support'),
    settings: navItems.filter((i) => i.section === 'settings')
  }), [navItems]);

  const settingsItem = navItems.find((i) => i.id === 'settings');

  /* ─── Active path helpers ─────────────────────────────────── */
  const isActive = useCallback((path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  }, [location.pathname]);

  const isSettingsActive = useMemo(() => {
    return settingsItem?.children?.some((c) => isActive(c.to)) ?? false;
  }, [settingsItem, isActive]);

  /* ─── Close helpers ───────────────────────────────────────── */
  const closeDropdowns = useCallback(() => setActiveDropdown(null), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const closeAll = useCallback(() => {
    setActiveDropdown(null);
    setMobileOpen(false);
  }, []);

  const toggleDropdown = useCallback((name) => {
    setActiveDropdown((cur) => (cur === name ? null : name));
  }, []);

  /* ─── Lock body scroll when mobile drawer is open ────────── */
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add('mobile-drawer-open');
    } else {
      document.body.classList.remove('mobile-drawer-open');
    }
    return () => document.body.classList.remove('mobile-drawer-open');
  }, [mobileOpen]);

  /* ─── Route change close ──────────────────────────────────── */
  useEffect(() => {
    closeDropdowns();
    closeMobile();
    setSettingsOpen(false);
  }, [location.pathname, closeDropdowns, closeMobile]);

  /* ─── Outside-click close ─────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      const inNotif    = notifRef.current?.contains(e.target);
      const inOnline   = onlineRef.current?.contains(e.target);
      const inUserMenu = userMenuRef.current?.contains(e.target);
      const inMobile   = mobileDrawerRef.current?.contains(e.target);
      const inSettings = settingsRef.current?.contains(e.target);

      // Close topbar dropdowns only when click is outside ALL their anchors
      if (!inNotif && !inOnline && !inUserMenu) {
        setActiveDropdown(null);
      }
      // Close settings dropdown when click is outside its anchor
      // (but not if the click is inside another dropdown anchor — already handled above)
      if (!inSettings) {
        setSettingsOpen(false);
      }
      // Close mobile drawer only when click is outside drawer and not on the hamburger
      if (!inMobile && !e.target.closest('.mobile-hamburger')) {
        setMobileOpen(false);
      }
    };
    // Use capture phase so we catch events before React synthetic handlers
    document.addEventListener('pointerdown', handler, true);
    return () => document.removeEventListener('pointerdown', handler, true);
  }, []);

  /* ─── Escape key close ────────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setActiveDropdown(null);
        setMobileOpen(false);
        setSettingsOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  /* ─── Bootstrap modal coordination ───────────────────────── */
  useEffect(() => {
    const handler = () => { setActiveDropdown(null); setMobileOpen(false); };
    document.addEventListener('show.bs.modal', handler);
    document.addEventListener('shown.bs.modal', handler);
    return () => {
      document.removeEventListener('show.bs.modal', handler);
      document.removeEventListener('shown.bs.modal', handler);
    };
  }, []);

  /* ─── Theme ───────────────────────────────────────────────── */
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const toggleTheme = useCallback(() => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('theme', next);
  }, [theme]);

  /* ─── Logout ──────────────────────────────────────────────── */
  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  /* ─── Notification click ──────────────────────────────────── */
  const handleNotificationClick = useCallback(async (e, notification) => {
    if (e.target.closest('[data-notification-action]')) return;
    markAsRead(notification.id);
    const target = resolveNotificationTarget(notification);
    if (!target?.path) {
      closeDropdowns();
      addNotification('لا توجد وجهة مرتبطة بهذا الإشعار.', 'warning', 'تنبيه');
      return;
    }
    const validation = await validateNotificationTarget(target);
    if (!validation.ok) {
      closeDropdowns();
      addNotification(validation.message, 'warning', 'تنبيه');
      return;
    }
    closeAll();
    navigate(target.path, {
      state: {
        ...(target.state || {}),
        sourceNotificationId: notification.id,
        sourceNotificationEvent: notification.event || null,
        sourceNotificationTarget: target
      }
    });
  }, [markAsRead, resolveNotificationTarget, closeDropdowns, addNotification, validateNotificationTarget, closeAll, navigate]);

  /* ─── Nav link click ──────────────────────────────────────── */
  const handleNavClick = useCallback(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
  }, []);

  const handleSettingsToggle = useCallback((e) => {
    e?.preventDefault?.();
    // NOTE: Do NOT stopPropagation here — the global pointerdown outside-click
    // handler needs to see clicks so it can close other panels.
    setSettingsOpen((v) => !v);
    setActiveDropdown(null);
  }, []);

  /* ─── Render a single nav link ────────────────────────────── */
  const renderLink = (item) => (
    <Link
      key={item.id}
      to={item.to}
      className={`snav-link${isActive(item.to) ? ' is-active' : ''}`}
      onClick={handleNavClick}
      title={item.label}
    >
      <span className="snav-icon">{item.icon}</span>
      <span className="snav-label">{item.label}</span>
    </Link>
  );

  /* ─── Render nav link (topbar) ────────────────────────────── */
  const renderTopbarLink = (item) => (
    <Link
      key={item.id}
      to={item.to}
      className={`tnav-link${isActive(item.to) ? ' is-active' : ''}`}
      onClick={handleNavClick}
      title={item.label}
    >
      <span className="tnav-icon">{item.icon}</span>
      <span>{item.label}</span>
    </Link>
  );

  /* ─── Render settings dropdown (topbar) ───────────── */
  const renderTopbarSettings = (item) => (
    <div key={item.id} className="tnav-dropdown-anchor" ref={settingsRef}>
      <button
        type="button"
        className={`tnav-link tnav-link--dropdown${(isSettingsActive || settingsOpen) ? ' is-active' : ''}`}
        onClick={handleSettingsToggle}
        aria-expanded={settingsOpen}
        aria-haspopup="menu"
      >
        <span className="tnav-icon">{item.icon}</span>
        <span>{settingsTriggerLabel}</span>
        <FaChevronDown className={`tnav-chevron${settingsOpen ? ' rotated' : ''}`} />
      </button>
      {settingsOpen && (
        <div className="tnav-dropdown-panel" role="menu" aria-label="قائمة الإعدادات">
          <div className="tnav-section-label">{settingsSectionLabel}</div>
          {item.children.map((child) => (
            <Link
              key={child.id}
              to={child.to}
              className={`tnav-child${isActive(child.to) ? ' is-active' : ''}`}
              onClick={() => { handleNavClick(); setSettingsOpen(false); }}
            >
              <span className="tnav-icon">{child.icon}</span>
              <span>{child.label}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div className={`app-shell${mobileOpen ? ' mobile-nav-open' : ''}`} data-theme={theme}>

      {/* ── Topbar ──────────────────────────────────────────────── */}
      <header className="app-topbar">

        {/* Brand — rightmost in RTL */}
        <Link to="/dashboard" className="topbar-brand" onClick={handleNavClick}>
          <span className="topbar-brand-icon"><FaTicketAlt /></span>
          <span className="topbar-brand-text">
            <strong>منصة التشغيل الداخلية</strong>
            <small>الدعم التقني والمشاريع</small>
          </span>
        </Link>

        {/* Mobile hamburger (visible ≤900 px) */}
        <button
          type="button"
          className="mobile-hamburger topbar-icon-btn"
          aria-label="فتح التنقل"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>

        {/* Center nav links (hidden on mobile) */}
        <nav className="topbar-nav" aria-label="التنقل الرئيسي">
          <div className="topbar-nav-center">
            {navSections.core.map(renderTopbarLink)}
            {navSections.support.map(renderTopbarLink)}
            {navSections.settings.map((item) =>
              item.type === 'group' ? renderTopbarSettings(item) : renderTopbarLink(item)
            )}
          </div>
        </nav>

        {/* Left actions — theme, notifications, online users, user menu */}
        <div className="topbar-actions">

          {/* Theme toggle */}
          <button
            type="button"
            className="topbar-icon-btn"
            onClick={toggleTheme}
            title={theme === 'light' ? 'تفعيل الوضع الداكن' : 'تفعيل الوضع الفاتح'}
          >
            {theme === 'light' ? <FaMoon /> : <FaSun />}
          </button>

          {/* Notifications */}
          <div className="topbar-dropdown-anchor" ref={notifRef}>
            <button
              type="button"
              className={`topbar-icon-btn has-badge${activeDropdown === 'notifications' ? ' is-active' : ''}`}
              onClick={() => toggleDropdown('notifications')}
              aria-label="الإشعارات"
            >
              <FaBell />
              {unreadCount > 0 && (
                <span className="topbar-badge">{formatNumber(unreadCount)}</span>
              )}
            </button>
            {activeDropdown === 'notifications' && (
              <div className="topbar-dropdown-panel notif-panel">
                <div className="panel-header">
                  <span className="panel-title">الإشعارات</span>
                  {unreadCount > 0 && (
                    <button type="button" className="panel-action-link" onClick={markAllAsRead}>
                      تحديد الكل كمقروء
                    </button>
                  )}
                </div>
                <div className="panel-list">
                  {notifications.length === 0 ? (
                    <div className="panel-empty">لا توجد إشعارات حالياً</div>
                  ) : (
                    notifications.slice(0, 15).map((n) => (
                      <button
                        type="button"
                        key={n.id}
                        className={`notif-item${n.read ? ' is-read' : ' is-unread'}`}
                        onClick={(e) => handleNotificationClick(e, n)}
                      >
                        <div className="notif-title">{n.title}</div>
                        <div className="notif-message">{n.message}</div>
                        <div className="notif-time">{formatDateTime(n.timestamp)}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Online Users */}
          <div className="topbar-dropdown-anchor" ref={onlineRef}>
            <button
              type="button"
              className={`topbar-chip-btn${activeDropdown === 'onlineUsers' ? ' is-active' : ''}`}
              onClick={() => toggleDropdown('onlineUsers')}
            >
              <span className="online-dot" />
              <span className="online-label">المتصلون</span>
              <span className="online-count">{formatNumber(onlineUsers?.total || 0)}</span>
            </button>
            {activeDropdown === 'onlineUsers' && (
              <div className="topbar-dropdown-panel">
                <div className="panel-header">
                  <span className="panel-title">المستخدمون المتصلون</span>
                  <span className="panel-badge">{formatNumber(onlineUsers?.total || 0)}</span>
                </div>
                <div className="panel-list">
                  {(onlineUsers?.users || []).length === 0 ? (
                    <div className="panel-empty">لا يوجد مستخدمون متصلون حالياً</div>
                  ) : (
                    (onlineUsers.users || []).slice(0, 20).map((u) => (
                      <div key={u.id} className="online-user-item">
                        <div className="online-user-avatar">{(u.full_name || 'U').charAt(0)}</div>
                        <div className="online-user-details">
                          <div className="online-user-name">{u.full_name}</div>
                          <div className="online-user-meta">
                            {ROLE_LABELS[normalizeRole(u.role)] || u.role} · {formatDateTime(u.last_seen_at)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="topbar-dropdown-anchor" ref={userMenuRef}>
            <button
              type="button"
              className={`topbar-user-btn${activeDropdown === 'userMenu' ? ' is-active' : ''}`}
              onClick={() => toggleDropdown('userMenu')}
            >
              <span className="topbar-avatar">{(user?.full_name || 'U').charAt(0)}</span>
              <span className="topbar-user-text">
                <strong>{user?.full_name || user?.username}</strong>
                <small>{roleLabel}</small>
              </span>
              <FaChevronDown className={`topbar-user-chevron${activeDropdown === 'userMenu' ? ' rotated' : ''}`} />
            </button>
            {activeDropdown === 'userMenu' && (
              <div className="topbar-dropdown-panel user-menu-panel">
                <div className="user-menu-header">
                  <div className="user-menu-avatar">{(user?.full_name || 'U').charAt(0)}</div>
                  <div>
                    <div className="user-menu-name">{user?.full_name || user?.username}</div>
                    <div className="user-menu-role">{roleLabel}</div>
                  </div>
                </div>
                <div className="user-menu-divider" />
                <button
                  type="button"
                  className="user-menu-item"
                  onClick={() => { navigate('/profile'); closeDropdowns(); }}
                >
                  <FaUser />
                  <span>الملف الشخصي</span>
                </button>
                <button
                  type="button"
                  className="user-menu-item danger"
                  onClick={handleLogout}
                >
                  <FaSignOutAlt />
                  <span>تسجيل الخروج</span>
                </button>
                </div>
            )}
          </div>

        </div>
      </header>

      {/* ── Page Content ──────────────────────────────────────────── */}
      <div className="app-main">
        <main className="app-content">
          <Outlet />
        </main>
      </div>

      {/* ── Mobile Overlay ────────────────────────────────────── */}
      {mobileOpen && (
        <div
          className="mobile-overlay"
          onClick={closeMobile}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile Drawer ─────────────────────────────────────── */}
      <aside
        className={`mobile-drawer${mobileOpen ? ' is-open' : ''}`}
        ref={mobileDrawerRef}
        aria-label="قائمة التنقل المحمولة"
      >
        <div className="mobile-drawer-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ width: 30, height: 30, borderRadius: 8, background: 'linear-gradient(135deg,var(--c-primary),var(--c-secondary))', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem', flexShrink: 0 }}><FaTicketAlt /></span>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
              <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>منصة التشغيل الداخلية</span>
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontWeight: 500 }}>
                الدعم التقني والمشاريع
              </span>
            </div>
          </div>
          <button
            type="button"
            className="topbar-icon-btn"
            onClick={closeMobile}
            aria-label="إغلاق القائمة"
          >
            <FaTimes />
          </button>
        </div>
        <div className="mobile-drawer-body">
          {navSections.core.length > 0 && (
            <div className="drawer-section">
              {navSections.core.map(renderLink)}
            </div>
          )}
          {navSections.support.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-label">الدعم التقني والمشاريع</div>
              {navSections.support.map(renderLink)}
            </div>
          )}
          {navSections.settings.length > 0 && (
            <div className="drawer-section">
              <div className="drawer-section-label">{settingsSectionLabel}</div>
              {navSections.settings.map((item) =>
                item.type === 'group'
                  ? item.children.map(renderLink)
                  : renderLink(item)
              )}
            </div>
          )}
        </div>
        <div className="mobile-drawer-footer">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,var(--c-primary),var(--c-secondary))', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem', flexShrink: 0 }}>{(user?.full_name || 'U').charAt(0)}</div>
            <div>
              <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>{user?.full_name || user?.username}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{roleLabel}</div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Layout;
